// Roadmap Template Service
// This service manages the roadmap templates data stored in Firestore

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const ROADMAP_TEMPLATES_COLLECTION = 'roadmapTemplates';

class RoadmapTemplateService {
  constructor() {
    // Track if migration has been attempted
    this.migrationAttempted = false;
  }

  // One-time migration from localStorage to Firestore
  async migrateFromLocalStorage() {
    // Only attempt migration once per session
    if (this.migrationAttempted) {
      return;
    }
    this.migrationAttempted = true;

    try {
      // Check if Firestore has any templates
      const templatesRef = collection(db, ROADMAP_TEMPLATES_COLLECTION);
      const q = query(templatesRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      // If Firestore already has templates, skip migration
      if (!querySnapshot.empty) {
        return;
      }

      // Check localStorage for old templates
      const stored = localStorage.getItem('roadmapTemplates');
      if (!stored) {
        return;
      }

      try {
        const localTemplates = JSON.parse(stored);
        if (!Array.isArray(localTemplates) || localTemplates.length === 0) {
          return;
        }

        console.log(`Migrating ${localTemplates.length} templates from localStorage to Firestore...`);
        
        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        // Migrate each template to Firestore
        for (const template of localTemplates) {
          // Remove the old numeric ID, Firestore will generate a new one
          const { id, ...templateData } = template;
          
          const templateToSave = {
            ...templateData,
            createdBy: userId || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };

          await addDoc(templatesRef, templateToSave);
        }

        console.log('✅ Migration complete! Templates migrated from localStorage to Firestore.');
        console.log('You can now clear localStorage if you want.');
        
      } catch (parseError) {
        console.error('Error parsing localStorage templates:', parseError);
      }
    } catch (error) {
      console.error('Error during migration:', error);
      // Don't throw - migration failure shouldn't break the app
    }
  }

  // Get all templates from Firestore
  async getAllTemplates() {
    try {
      // Attempt migration if Firestore is empty
      await this.migrateFromLocalStorage();

      const templatesRef = collection(db, ROADMAP_TEMPLATES_COLLECTION);
      const q = query(templatesRef, orderBy('order', 'asc'));
      
      const querySnapshot = await getDocs(q);
      const templates = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      return templates;
    } catch (error) {
      console.error('Error getting roadmap templates:', error);
      throw error;
    }
  }

  // Get templates by department
  async getTemplatesByDepartment(department) {
    try {
      const templatesRef = collection(db, ROADMAP_TEMPLATES_COLLECTION);
      let q;
      
      if (department === 'all') {
        q = query(templatesRef, orderBy('order', 'asc'));
      } else {
        q = query(
          templatesRef, 
          where('department', '==', department),
          orderBy('order', 'asc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const templates = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      return templates;
    } catch (error) {
      console.error('Error getting templates by department:', error);
      throw error;
    }
  }

  // Get a single template by ID
  async getTemplateById(id) {
    try {
      const templateRef = doc(db, ROADMAP_TEMPLATES_COLLECTION, id);
      const templateDoc = await getDoc(templateRef);
      
      if (templateDoc.exists()) {
        return {
          id: templateDoc.id,
          ...templateDoc.data(),
          createdAt: templateDoc.data().createdAt?.toDate() || null,
          updatedAt: templateDoc.data().updatedAt?.toDate() || null
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting template by ID:', error);
      throw error;
    }
  }

  // Add a new template
  async addTemplate(template) {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      // Get current max order
      const allTemplates = await this.getAllTemplates();
      const maxOrder = allTemplates.length > 0 
        ? Math.max(...allTemplates.map(t => t.order || 0), 0)
        : 0;

      const templateToSave = {
        ...template,
        order: template.order || maxOrder + 1,
        createdBy: userId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, ROADMAP_TEMPLATES_COLLECTION), templateToSave);
      
      return {
        id: docRef.id,
        ...template,
        order: template.order || maxOrder + 1,
        createdBy: userId || null,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error adding template:', error);
      throw error;
    }
  }

  // Update an existing template
  async updateTemplate(id, updates) {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      const templateRef = doc(db, ROADMAP_TEMPLATES_COLLECTION, id);
      
      // Check if template exists
      const templateDoc = await getDoc(templateRef);
      if (!templateDoc.exists()) {
        throw new Error('Template not found');
      }

      const updateToSave = {
        ...updates,
        updatedBy: userId || null,
        updatedAt: serverTimestamp()
      };

      await updateDoc(templateRef, updateToSave);
      
      return {
        id: id,
        ...updates,
        updatedBy: userId || null,
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  // Delete a template
  async deleteTemplate(id) {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('User must be authenticated to delete templates');
      }

      const templateRef = doc(db, ROADMAP_TEMPLATES_COLLECTION, id);
      
      // Check if template exists
      const templateDoc = await getDoc(templateRef);
      if (!templateDoc.exists()) {
        throw new Error('Template not found');
      }

      await deleteDoc(templateRef);
      
      return { success: true, id: id };
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  // Update template orders
  async updateTemplateOrders(templates, orderField = 'order') {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      const updatePromises = templates.map((template, index) => {
        const templateRef = doc(db, ROADMAP_TEMPLATES_COLLECTION, template.id);
        const updates = {
          order: index + 1,
          updatedBy: userId || null,
          updatedAt: serverTimestamp()
        };
        
        // Remove old order fields if they exist (we'll do this in a separate update if needed)
        return updateDoc(templateRef, updates);
      });

      await Promise.all(updatePromises);
      return true;
    } catch (error) {
      console.error('Error updating template orders:', error);
      throw error;
    }
  }

  // Set the same status on many templates at once
  async updateTemplateStatuses(templateIds, status) {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;

      const updatePromises = templateIds.map((templateId) => {
        const templateRef = doc(db, ROADMAP_TEMPLATES_COLLECTION, templateId);
        return updateDoc(templateRef, {
          status,
          updatedBy: userId || null,
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(updatePromises);
      return true;
    } catch (error) {
      console.error('Error updating template statuses:', error);
      throw error;
    }
  }

  // Get roadmap items for a specific department
  async getRoadmapItems(department) {
    const templates = await this.getTemplatesByDepartment(department);
    return templates.map(template => ({
      id: template.id,
      title: template.title,
      description: template.description,
      goal: template.goal,
      department: template.department,
      tasks: template.tasks || [],
      navigators: template.navigators || [],
      assignees: template.assignees || []
    }));
  }

  // Convert template tasks to actual task objects for creation
  convertTemplateTasksToActualTasks(templateTasks, userId, accountId = 'internal') {
    return templateTasks.map(task => ({
      title: task.title,
      description: task.description,
      dueDate: task.dueDate,
      estimatedHours: task.estimatedHours || 0,
      estimatedMinutes: task.estimatedMinutes || 0,
      assignees: task.assignees || [],
      accountId: task.accountId || accountId,
      files: task.files || [],
      completed: false,
      status: 'Not Done',
      createdBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      completedAt: null
    }));
  }
}

// Create a singleton instance
const roadmapTemplateService = new RoadmapTemplateService();

export default roadmapTemplateService;
